import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { Toaster } from './components/ui/sonner';
import { ThemeProvider } from './context/ThemeContext';
import CookieConsent from './components/CookieConsent';
import BackToTop from './components/BackToTop';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import AuthCallback from './pages/AuthCallback';
import Feed from './pages/Feed';
import PostDetail from './pages/PostDetail';
import CreatePost from './pages/CreatePost';
import Leaderboard from './pages/Leaderboard';
import Chat from './pages/Chat';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import DonationSuccess from './pages/DonationSuccess';
import CorporateFund from './pages/CorporateFund';
import Terms from './pages/Terms';
import Contact from './pages/Contact';
import Advertise from './pages/Advertise';
import AdminDashboard from './pages/AdminDashboard';
import SuccessStories from './pages/SuccessStories';
import Marketplace from './pages/Marketplace';
import MarketplaceDetail from './pages/MarketplaceDetail';
import Privacy from './pages/Privacy';
import ForgotPassword from './pages/ForgotPassword';
import NotFound from './pages/NotFound';
import MyDashboard from './pages/MyDashboard';
import DonateTime from './pages/DonateTime';
import TimeOfferDetail from './pages/TimeOfferDetail';
import HeartDesires from './pages/HeartDesires';
import WishDetail from './pages/WishDetail';

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-800"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// App Router with session_id detection
const AppRouter = () => {
  const location = useLocation();
  
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/feed" element={<Feed />} />
      <Route path="/posts/:postId" element={<PostDetail />} />
      <Route path="/create-post" element={
        <ProtectedRoute>
          <CreatePost />
        </ProtectedRoute>
      } />
      <Route path="/leaderboard" element={<Leaderboard />} />
      <Route path="/corporate-fund" element={<CorporateFund />} />
      <Route path="/chat" element={
        <ProtectedRoute>
          <Chat />
        </ProtectedRoute>
      } />
      <Route path="/profile/:userId" element={<Profile />} />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Settings />
        </ProtectedRoute>
      } />
      <Route path="/donation/success" element={<DonationSuccess />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/advertise" element={<Advertise />} />
      <Route path="/success-stories" element={<SuccessStories />} />
      <Route path="/marketplace" element={<Marketplace />} />
      <Route path="/marketplace/:id" element={<MarketplaceDetail />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/dashboard" element={<MyDashboard />} />
      <Route path="/donate-time" element={<DonateTime />} />
      <Route path="/donate-time/:id" element={<TimeOfferDetail />} />
      <Route path="/heart-desires" element={<HeartDesires />} />
      <Route path="/heart-desires/:id" element={<WishDetail />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AppRouter />
          <Toaster position="top-right" richColors />
          <CookieConsent />
          <BackToTop />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
